import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { COOKIE_STORAGE } from '@helpers';

interface SideMenuUserProps {
    user?: any;
}

interface Point {
    x: number;
    y: number;
}

const SideMenuUser: React.FC<SideMenuUserProps> = ({ user }) => {
    const logoutUser = (): any => {
        COOKIE_STORAGE.removeItem('applicationAuth');
        window.location.href = '/login';
    };

    // Generate a unique pattern based on user data
    const generatePattern = useMemo(() => {
        if (!user) return '';

        // Use user's name or ID as seed
        const seed = user.id || `${user.firstName}${user.lastName}`;
        const hash = Array.from<string, number>(seed, (char) => {
            const acc = 0;
            return char.charCodeAt(0) + ((acc << 5) - acc);
        }).reduce((acc, val) => acc + val, 0);

        // Use hash to determine pattern type
        const patternType = hash % 4; // 4 different pattern types
        const radius = 12;

        // Helper function to get point on circle
        const getPoint = (angle: number, r: number): Point => ({
            x: Math.cos(angle) * r,
            y: Math.sin(angle) * r,
        });

        let pathCommands = '';

        switch (patternType) {
            case 0: {
                // Circular wave pattern
                const numPoints = 24;
                const points: Point[] = [];
                for (let i = 0; i < numPoints; i++) {
                    const angle = (i * 2 * Math.PI) / numPoints;
                    const waveRadius = radius * (0.8 + Math.sin(i * 4 + hash) * 0.2);
                    points.push(getPoint(angle, waveRadius));
                }
                pathCommands = `M ${points.map((p) => `${p.x},${p.y}`).join(' L ')} Z`;
                break;
            }
            case 1: {
                // Spiral pattern
                const numSpirals = 3;
                const numPoints = 30;
                const points: Point[] = [];
                for (let i = 0; i < numPoints; i++) {
                    const angle = (i * 2 * Math.PI * numSpirals) / numPoints;
                    const r = (radius * i) / numPoints;
                    points.push(getPoint(angle, r));
                }
                pathCommands = `M ${points.map((p) => `${p.x},${p.y}`).join(' L ')}`;
                break;
            }
            case 2: {
                // Abstract curves
                const numCurves = 5;
                const paths: string[] = [];
                for (let i = 0; i < numCurves; i++) {
                    const angle = (i * 2 * Math.PI) / numCurves;
                    const r1 = radius * (0.5 + (hash % 5) / 10);
                    const r2 = radius * (0.7 + (hash % 3) / 10);
                    const p1 = getPoint(angle, r1);
                    const p2 = getPoint(angle + Math.PI / numCurves, r2);
                    paths.push(`M ${p1.x},${p1.y} Q 0,0 ${p2.x},${p2.y}`);
                }
                pathCommands = paths.join(' ');
                break;
            }
            default: {
                // Geometric pattern
                const numPoints = 5 + (hash % 4);
                const points: Point[] = [];
                const innerPoints: Point[] = [];
                for (let i = 0; i < numPoints; i++) {
                    const angle = (i * 2 * Math.PI) / numPoints;
                    points.push(getPoint(angle, radius));
                    innerPoints.push(getPoint(angle + Math.PI / numPoints, radius * 0.4));
                }
                const allPoints = points.flatMap((p, i) => [p, innerPoints[i]]);
                pathCommands = `M ${allPoints.map((p) => `${p.x},${p.y}`).join(' L ')} Z`;
            }
        }

        return pathCommands;
    }, [user]);

    return (
        <div className="aside-user d-flex align-items-sm-center justify-content-center py-5">
            <div className="flex-row-fluid flex-wrap">
                <div className="d-flex align-items-center flex-stack">
                    <div className="d-flex align-items-center me-2">
                        <div className="me-3" style={{ width: '32px', height: '32px' }}>
                            <svg width="32" height="32" viewBox="-15 -15 30 30" style={{ borderRadius: '50%' }}>
                                <path d={generatePattern} fill="#d41117" transform="rotate(0)" />
                            </svg>
                        </div>
                        <Link to="/my-account" className="text-gray-800 text-hover-primary fs-6 fw-bold lh-0">
                            {`${user?.firstName} ${user?.lastName}`}
                        </Link>
                    </div>
                    <button className="btn btn-icon btn-active-color-primary me-n4" onClick={logoutUser}>
                        <i className="svg-icon-2 svg-icon-gray-400 fas fa-arrow-right-from-bracket" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SideMenuUser;
